import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Statistic,
  Table, Alert, Tooltip, Progress, InputNumber,
} from 'antd';
import {
  AuditOutlined, ReloadOutlined, EnvironmentOutlined, EyeInvisibleOutlined,
  WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { getEvents, getCoverageAudit, getPhantomTags } from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const HEALTH = {
  ok:             { color: '#10b981', label: 'Healthy',         icon: <CheckCircleOutlined /> },
  no_traffic:     { color: '#94a3b8', label: 'No traffic yet',   icon: <EyeInvisibleOutlined /> },
  no_readers:     { color: '#ef4444', label: 'No readers',       icon: <WarningOutlined /> },
  silent_readers: { color: '#f59e0b', label: 'Silent readers',   icon: <WarningOutlined /> },
};

export default function CoverageAudit() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [zones, setZones] = useState([]);
  const [totals, setTotals] = useState({});
  const [phantoms, setPhantoms] = useState([]);
  const [phantomSummary, setPhantomSummary] = useState({});
  const [maxReads, setMaxReads] = useState(3);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    Promise.all([
      getCoverageAudit({ event_id: eventId }),
      getPhantomTags({ event_id: eventId, max_reads: maxReads }),
    ]).then(([c, p]) => {
      setZones(c.data.data || []);
      setTotals(c.data.totals || {});
      setPhantoms(p.data.data || []);
      setPhantomSummary(p.data.summary || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eventId, maxReads]);

  useEffect(() => { load(); }, [load]);

  const issuesCount = zones.filter(z => z.health !== 'ok' && z.health !== 'no_traffic').length;
  const phantomPct = phantomSummary.total_unique_tags > 0
    ? Math.round((phantomSummary.phantom_count / phantomSummary.total_unique_tags) * 100)
    : 0;

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <AuditOutlined style={{ marginRight: 8, color: TEAL }} />Coverage & Phantom Audit
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Find zone coverage gaps and detect phantom (false-positive) tag reads
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Registered" value={totals.total_registered || 0} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Seen at any zone" value={totals.total_seen || 0}
              suffix={<Text type="secondary" style={{ fontSize: 11 }}>{totals.total_registered ? Math.round((totals.total_seen / totals.total_registered) * 100) : 0}%</Text>}
              valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #ef4444' }}>
            <Statistic title="Coverage Issues" value={issuesCount} suffix="zones"
              prefix={<WarningOutlined />} valueStyle={{ color: issuesCount ? '#ef4444' : '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #f59e0b' }}>
            <Statistic title="Phantom Tags" value={phantomSummary.phantom_count || 0}
              suffix={<Text type="secondary" style={{ fontSize: 11 }}>{phantomPct}% of unique tags</Text>}
              prefix={<EyeInvisibleOutlined />} valueStyle={{ color: '#f59e0b' }} />
          </Card>
        </Col>
      </Row>

      {issuesCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={`${issuesCount} zone(s) have coverage problems`}
          description="Some zones have no readers, silent readers or no traffic. Check your reader fleet and verify each zone has at least one online reader."
        />
      )}

      <Row gutter={[16, 16]}>
        {/* Zone Coverage */}
        <Col xs={24} lg={14}>
          <Card title={<Space><EnvironmentOutlined style={{ color: TEAL }} /> Zone Coverage Map</Space>}
            style={{ borderRadius: 10 }}>
            {zones.length === 0 ? <Empty /> : (
              <Table
                dataSource={zones}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  { title: 'Zone', dataIndex: 'location_name', render: (v, r) => (
                    <Space>
                      <EnvironmentOutlined style={{ color: TEAL }} />
                      <Text strong>{v}</Text>
                      <Tag>{r.location_type || 'Zone'}</Tag>
                    </Space>
                  )},
                  { title: 'Health', dataIndex: 'health', width: 150,
                    render: v => {
                      const h = HEALTH[v] || HEALTH.ok;
                      return <Tag icon={h.icon} color={v === 'ok' ? 'green' : v === 'no_readers' ? 'red' : v === 'silent_readers' ? 'orange' : 'default'}>
                        {h.label}
                      </Tag>;
                    }
                  },
                  { title: 'Readers', width: 90, align: 'center',
                    render: (_, r) => (
                      <Tooltip title={`${r.readers_silent} silent of ${r.readers_at_zone} total`}>
                        <Tag color={r.readers_silent > 0 ? 'orange' : r.readers_at_zone > 0 ? 'green' : 'red'}>
                          {r.readers_silent > 0 ? `${r.readers_at_zone - r.readers_silent}/${r.readers_at_zone}` : r.readers_at_zone}
                        </Tag>
                      </Tooltip>
                    )
                  },
                  { title: 'Visitors', dataIndex: 'unique_visitors', width: 90, align: 'center',
                    render: v => <Tag color="cyan">{v}</Tag> },
                  { title: 'Coverage', dataIndex: 'coverage_pct', width: 130,
                    render: v => <Progress percent={v} size="small" strokeColor={
                      v >= 50 ? TEAL : v >= 20 ? '#f59e0b' : '#94a3b8'
                    } /> },
                  { title: 'Total Scans', dataIndex: 'total_scans', width: 100, align: 'right',
                    render: v => <Text type="secondary">{(v || 0).toLocaleString()}</Text> },
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Phantom Tags */}
        <Col xs={24} lg={10}>
          <Card title={<Space><EyeInvisibleOutlined style={{ color: '#f59e0b' }} /> Phantom / False Reads</Space>}
            style={{ borderRadius: 10 }}
            extra={
              <Tooltip title="Tags read fewer times than this threshold are flagged as phantom (likely interference or distant reads)">
                <Space size={4}>
                  <Text style={{ fontSize: 11 }}>Max reads:</Text>
                  <InputNumber min={1} max={20} value={maxReads} onChange={v => setMaxReads(v || 3)} size="small" style={{ width: 60 }} />
                </Space>
              </Tooltip>
            }>
            {phantoms.length === 0 ? (
              <Empty description={<Text type="success">No phantom tags ✓</Text>} />
            ) : (
              <Table
                dataSource={phantoms}
                rowKey="rfid_tag"
                size="small"
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: 'Tag', dataIndex: 'rfid_tag',
                    render: v => <Text code style={{ fontSize: 10 }}>...{v.slice(-12)}</Text>
                  },
                  {
                    title: 'Reads', dataIndex: 'read_count', width: 60, align: 'center',
                    render: v => <Tag color={v === 1 ? 'red' : 'orange'}>{v}</Tag>
                  },
                  {
                    title: 'RSSI', dataIndex: 'avg_rssi', width: 80, align: 'right',
                    render: v => v != null ? <Text style={{ color: v >= -65 ? '#10b981' : v >= -75 ? '#f59e0b' : '#ef4444' }}>{v} dBm</Text> : '—'
                  },
                  {
                    title: 'Last Seen', dataIndex: 'last_seen', width: 90,
                    render: v => <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(v).format('HH:mm:ss')}</Text>
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
